import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ValidationErrors, ValidatorFn } from '@angular/forms';
import { sortBy } from 'lodash';
import { LocationService } from '../../../../service/location.service';
import { UserLoginService } from '../../../../service/user-login.service';
import {
  AssignmentDetails,
  IncidentFormContext,
  IncidentFormError,
  IncidentFormGroup,
  IncidentFormValues,
  LiveCamera,
  LocationResponse,
  ReviewStatus,
  TransactionEvent,
  TimelineRow,
  VideoClip,
} from '../../../../types';
import { atLeastOneExtraLinkValidator, defaultIncidentFormValues, incidentFormValidator } from '../review-submit-form/form-utils';
import { ReviewFormService } from '../review-submit-form/review-form.service';
import { AssignmentDetailsStore } from '../investigation-assignment/assignment-details.store';

@Component({
  selector: 'app-grocery-incident-form',
  templateUrl: './grocery-incident-form.component.html',
  styleUrl: './grocery-incident-form.component.scss',
  standalone: false,
})
export class GroceryIncidentFormComponent implements OnInit, AfterViewInit, OnDestroy {
  public reviewFormService = inject(ReviewFormService);
  private locationService = inject(LocationService);
  private userLoginService = inject(UserLoginService);
  public assignmentDetailsStore = inject(AssignmentDetailsStore);
  public isSubmitting = signal<boolean>(false);
  public isLoading = signal<boolean>(false);
  public locationId = signal<number | undefined>(undefined);
  public location = signal<LocationResponse | undefined>(undefined);
  public liveCameras = signal<LiveCamera[] | undefined>(undefined);
  public instructions = signal<string | undefined>(undefined);
  public notes = signal<string | undefined>(undefined);
  public videoClips = signal<VideoClip[]>([]);
  public transactionEvents = signal<TransactionEvent[]>([]);
  public videoExpanded = signal<boolean>(false);
  public reviewFormInitialValues = signal<IncidentFormValues | undefined>(undefined);
  public isAssignedToOther = signal<boolean | undefined>(undefined);
  public locationTimezone = input<string | undefined>();
  public formRef = viewChild<TemplateRef<unknown>>('formBody');
  public contextReady = output<IncidentFormContext>();
  public contextError = output<unknown>();
  public formRefReady = output<TemplateRef<unknown>>();

  public timelineRows = computed<TimelineRow[]>(() => {
    return sortBy(this.transactionEvents(), (event) => new Date(event.event_time).getTime()).map((event) => {
      const data = event.details?.data;
      const itemName = data?.item_name ?? data?.item_code;
      const itemValue = data?.total ?? data?.price;
      return {
        when: event.event_type,
        timestamp: event.event_time,
        items: itemName ? `${itemName} ($${Number(itemValue ?? 0).toFixed(2)})` : '-',
        highlight: false,
      };
    });
  });

  constructor() {
    effect(() => {
      const details = this.assignmentDetailsStore.details();
      if (!details) return;
      this.loadGroceryAssignment(details);
    });
  }

  public ngOnInit() {
    this.reviewFormService.form.setValidators(this.groceryAssignmentValidator);
    this.reviewFormService.form.updateValueAndValidity({ emitEvent: false });
  }

  public ngAfterViewInit(): void {
    this.formRefReady.emit(this.formRef());
  }

  public ngOnDestroy(): void {
    this.reviewFormService.form.setValidators(incidentFormValidator);
  }

  private loadGroceryAssignment(details: AssignmentDetails) {
    if (!details) {
      this.contextError.emit(new Error('Assignment details not available'));
      return;
    }

    this.locationId.set(details.location_id);
    this.instructions.set(details.instructions);
    this.liveCameras.set(details.live_streams);
    this.notes.set(details.notes ?? '');
    this.videoClips.set(details.video_clips ?? []);
    this.transactionEvents.set(details.transaction_events ?? []);

    const isAssignedToOther =
      details.status === ReviewStatus.Assigned &&
      details.user_id !== null &&
      details.user_id !== this.userLoginService.currentProfile()?.user_id &&
      !this.userLoginService.currentUserIsAdmin();
    this.reviewFormService.form.setErrors(isAssignedToOther ? { [IncidentFormError.ASSIGNED_TO_OTHER]: true } : null);

    this.reviewFormInitialValues.set({ ...defaultIncidentFormValues });
    this.reviewFormService.caseImages.set(details.case_images);

    this.contextReady.emit({
      locationId: details.location_id,
      instanceAtDate: undefined,
      form: this.reviewFormService.form,
    });

    this.locationService.getLocation({ locationId: details.location_id }).subscribe((location) => this.location.set(location));
  }

  private readonly groceryAssignmentValidator: ValidatorFn = (form: IncidentFormGroup): ValidationErrors | null => {
    if (!form.controls.isTheft.value) return null;
    const baseErrors = incidentFormValidator(form) || {};
    // in grocery review we can assume a video clip was included by the LP app (from review_attachments),
    // so, the intention is to only require "extra links" when there were no video clips attached (if it happens for some reason)
    const needsExtraLink = this.videoClips().length === 0 ? atLeastOneExtraLinkValidator(form.controls.extraLinks) : null;
    const extraLinkErrors = needsExtraLink ? { [IncidentFormError.EXTRA_LINKS_REQUIRED]: true } : {};
    const merged = { ...baseErrors, ...extraLinkErrors } as ValidationErrors;
    return Object.keys(merged).length > 0 ? merged : null;
  };
}
